import { getToken, removeToken } from '@/utils/auth';

const BASE_URL = '';

/** 带认证头的 fetch 封装：自动附加 Bearer JWT，401 时清除登录态并跳转登录页（跳过 auth 接口） */
export async function authFetch(path: string, init?: RequestInit): Promise<Response> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(init?.headers as Record<string, string> | undefined),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const res = await fetch(`${BASE_URL}${path}`, { ...init, headers });
  if (res.status === 401 && !path.startsWith('/api/auth/')) {
    removeToken();
    if (!window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
  }
  return res;
}

export async function get<T>(path: string): Promise<T> {
  const res = await authFetch(`${BASE_URL}${path}`);
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
  return res.json() as Promise<T>;
}

export async function post<T>(path: string, body?: unknown): Promise<T> {
  const res = await authFetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`POST ${path} failed: ${res.status}`);
  return res.json() as Promise<T>;
}

export function connectWebSocket(taskId: string): WebSocket {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  const ws = new WebSocket(`${protocol}//${host}/ws/tasks/${taskId}`);
  return ws;
}

export type SSEEvent = { type: string; data: Record<string, unknown> | string };

/** 停止会话当前生成任务（waiting 删除，active 通过 cancel signal 中断）。 */
export async function cancelSessionGeneration(sessionId: string): Promise<void> {
  await post(`/api/agent/session/${sessionId}/cancel`);
}

/** 素材生成段失败后单独补图重试：仅重新生成缺失槽位，补齐后自动从下一段续跑。 */
export async function retryFailedAssets(sessionId: string): Promise<{ success: boolean; data?: { started: boolean; missing: string[] }; error?: string }> {
  return post(`/api/agent/session/${sessionId}/assets/retry`);
}

/** 生成配置（随确认一并提交给后端；对接 P0 langgraph StreamGenConfig）。 */
export interface StreamGenConfig {
  generationMode?: 'auto' | 'template' | 'blueprint' | 'cocos' | 'cocos-generation' | 'video';
  imageProvider?: 'jimeng' | 'wanxiang';
  batchConfig?: { count: number; dimensions: Array<{ name: string; values: string[] }> };
  customAssets?: string[];
  referenceImageUrls?: string[];
  skillContent?: string;
  videoFilePaths?: string[];
  endCardFilePaths?: string[];
  /** 视频素材确认卡片调整：拖拽后的视频顺序（fileName 序列）与手动指定的图片槽位 */
  videoMaterialAdjustment?: { videoOrder: string[]; imageRoles: Array<{ fileName: string; role: string }>; videoOrderIndexes?: number[] };
  /** 聊天附件图片（有序），供后端识别"第一张图作参考图"等自然语言用途描述 */
  uploadedImages?: Array<{ assetId: string; url: string; fileName: string; type?: string; subcategory?: string }>;
  /** 聊天附件视频（有序），随消息透传展示在聊天记录中 */
  uploadedVideos?: Array<{ filePath: string; url?: string; fileName: string; originalName?: string }>;
  /** 重复指令二次确认选择：reuse 复用上次产物 / regenerate 绕过缓存生成不一样的效果 */
  duplicateChoice?: 'reuse' | 'regenerate';
  /** 基线版本ID：基于选中的版本生成新版本（微调/迭代），而不是总是使用最新版本 */
  baseVersionId?: string;
  /** 视频方案确认（FEAT-20260907-VIDEO-PLAN）：用户确认方案后通知后端进入阶段三（入队生成） */
  videoPlanConfirm?: boolean;
}

/**
 * SSE streaming chat — yields events as they arrive.
 * Pass `signal` to abort mid-stream. `config` carries generation options (mode/assets/etc.).
 */
export async function* streamChat(
  message: string,
  sessionId?: string | null,
  signal?: AbortSignal,
  confirm?: boolean,
  config?: StreamGenConfig,
): AsyncGenerator<SSEEvent> {
  const res = await fetch(`${BASE_URL}/api/agent/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
    },
    body: JSON.stringify({ message, sessionId, confirm, ...(config ?? {}) }),
    signal,
  });
  if (!res.ok || !res.body) {
    // BUG-20260909-PW-001: 请求失败时带上后端错误详情（ValidationPipe 400 message 等），
    // 卡片 metadata.error 不再是无信息 'Stream failed'，便于定位脏字段等根因
    let detail = '';
    if (res.body) {
      try {
        const body = (await res.clone().json()) as { message?: unknown; error?: unknown };
        detail = typeof body?.message === 'string' ? body.message
          : Array.isArray(body?.message) ? (body.message as unknown[]).join('; ')
          : typeof body?.error === 'string' ? body.error : '';
      } catch { /* 非 JSON 错误体 */ }
    }
    throw new Error(detail ? `Stream failed: ${detail}` : `Stream failed (HTTP ${res.status})`);
  }

  const reader = res.body.getReader();
  if (signal) {
    signal.addEventListener(
      'abort',
      () => {
        try { reader.cancel(); } catch { /* noop */ }
      },
      { once: true },
    );
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = '';
  let currentData = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        currentData = line.slice(6);
      } else if (line === '' && currentEvent) {
        try {
          const parsed = JSON.parse(currentData);
          console.log('[SSE] Raw event:', currentEvent, parsed);
          yield { type: currentEvent, data: parsed };
        } catch {
          console.log('[SSE] Raw event (unparsed):', currentEvent, currentData);
          yield { type: currentEvent, data: currentData };
        }
        currentEvent = '';
        currentData = '';
      }
    }
  }
}
